import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';

import { useDeepCompareMemo } from '@clatter/platform';
import { useGetFolderByIdQuery, useGetFolderPathQuery } from '../../../../services';
import { useDocumentZipDownload } from '../../../../hooks';

import { FolderManagerMoveDialog } from '../../../FolderManager/Components/FolderManagerMoveDialog';
import { useWorkspace } from 'apps/ng/src/app/context/WorkspaceProvider';

interface OutputSelectorProps {
  // longpullId: number | null;
  rootFolderId: number | undefined;
  projectName: string | undefined;
  onChange: (output: { type: string; path: string; folderId?: number }) => void;
  initialOutput?: { type: string; path: string } | null;
}

const initialMoveDialogState: IMoveDialogState = {
  opened: false,
  rootFolder: null,
  selectedItems: [],
};

const StyledRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  /* padding: ${({ theme }) => theme.spacing.small}; */
  gap: ${({ theme }) => theme.spacing.medium};

  font-size: ${({ theme }) => theme.fontSize.default};

  label {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.small};
    min-width: 55px;
  }

  input {
    margin: 0;
  }
`;

const StyledOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
`;

const StyledFolderInput = styled.input`
  margin-left: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.small};
  width: 100%;
  flex: 1;
`;

const StyledDownloadLink = styled.a<{ $disabled: boolean }>`
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  align-items: center;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  color: ${({ theme, $disabled }) => ($disabled ? theme?.palette?.gray : theme?.palette?.textLinkColor)};

  &:hover {
    text-decoration: underline;
    transition: all 0.2s ease-in-out;
  }
`;

export const OutputSelector: FC<OutputSelectorProps> = ({ onChange, projectName, rootFolderId, initialOutput }) => {
  const { downloadZip } = useDocumentZipDownload();

  const { isDocumentsGenerated, longPullItemId: longpullId } = useWorkspace();

  const [selectedFolderID, setSelectedFolderId] = useState(rootFolderId);
  const [moveDialogState, setMoveDialogState] = useState(initialMoveDialogState);
  const [selectedOutput, setSelectedOutput] = useState<{ type: string; path: string; folderId?: number | null }>(
    initialOutput || { type: 'zip', path: '' },
  );
  const [isZipEnabled, setIsZipEnabled] = useState(false);

  const { data: rootFolderData } = useGetFolderByIdQuery({ folderId: rootFolderId! }, { skip: !rootFolderId });

  const { data: folderBreadcrumbs } = useGetFolderPathQuery(
    { folderId: selectedFolderID! },
    { skip: !selectedFolderID },
  );

  const zipFileName = `${projectName}.zip`;

  const folderDestinationPath = useDeepCompareMemo(
    () => (folderBreadcrumbs || []).map((item) => item?.name).join('/'),
    [folderBreadcrumbs],
  );

  const outputTypeOptions = [
    { value: 'folder', label: 'Folder' },
    { value: 'zip', label: 'Zip' },
  ];

  const handleOutputTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const outputType = event.target.value;
    const newOutput = { type: outputType, path: selectedOutput.path, folderId: selectedFolderID };

    setIsZipEnabled(false);

    setSelectedOutput(newOutput);
    onChange(newOutput);
  };

  const handleDownloadZip = async () => {
    await downloadZip({ longpullId: longpullId!, fileName: zipFileName });
  };

  const handleFolderSelectClick = () => {
    if (!rootFolderId || !folderBreadcrumbs?.[0]?.id) {
      console.warn('Could not open folder selector: no root folder ID!');
      return;
    }

    setMoveDialogState({
      ...initialMoveDialogState,
      opened: true,
      // for now, we need always start from user's root instead of selected one
      // because how we handle hierarchy navigation in the move dialog
      rootFolder: rootFolderData as FolderManagerItem,
    });
  };

  const handlePickDestinationFolder = (data: IMoveDialogOnDismiss | null) => {
    if (!data) {
      setMoveDialogState(initialMoveDialogState);
      return;
    }

    const targetFolderId = data.destinationId as number;
    setSelectedFolderId(targetFolderId);
    onChange({ type: 'folder', path: '', folderId: targetFolderId });
    setMoveDialogState(initialMoveDialogState);
  };

  //region EFFECTS
  useEffect(() => {
    setIsZipEnabled(false);
  }, [longpullId]);
  //endregion

  return (
    <>
      <StyledRadioGroup>
        {outputTypeOptions.map((option) => (
          <StyledOption key={option.value}>
            <label>
              <input
                type="radio"
                name="outputType"
                value={option.value}
                checked={selectedOutput.type === option.value}
                onChange={handleOutputTypeChange}
              />
              {option.label}
            </label>

            {option.value === 'folder' && (
              <StyledFolderInput
                type="text"
                value={folderDestinationPath}
                placeholder="Enter folder path"
                onClick={handleFolderSelectClick}
                disabled={selectedOutput.type !== 'folder' || !rootFolderId}
              />
            )}

            {option.value === 'zip' && (
              <StyledDownloadLink $disabled={!isDocumentsGenerated && !isZipEnabled} onClick={handleDownloadZip}>
                <span>{zipFileName}</span>

                <CloudDownloadOutlinedIcon />
              </StyledDownloadLink>
            )}
          </StyledOption>
        ))}
      </StyledRadioGroup>

      {moveDialogState?.opened && (
        <FolderManagerMoveDialog
          dialogState={moveDialogState}
          submitLabel="Select"
          onDismiss={handlePickDestinationFolder}
          includeUserRoot
          targetOnly
        />
      )}
    </>
  );
};
